/**
 * DemoLumaPanel — Luma chat for read-only demo sessions.
 *
 * Demo sessions must NOT create persistent chat sessions (that would write rows
 * into the shared golden demo tenant and is blocked by the read-only guard).
 * Instead this panel drives the ephemeral one-shot endpoint
 * (`POST /api/v1/ai-chat/message`, persist=false) with client-side history.
 * Module tools are forced read-only server-side for demo sessions, so Luma can
 * browse the demo data but never mutate it. See
 * docs/platform/demo-without-login-design.md.
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ChatBox } from './ChatBox';
import type { ChatMessage, StreamingState } from './types';
import { useResponsive } from '../../hooks/useResponsive';
import { useDirection } from '../../contexts/DirectionContext';
import { API_BASE_URL } from '../../api/config';
import { AUTH_STORAGE_KEYS } from '../../auth/storageKeys';

export interface DemoLumaPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  inputPlaceholder: string;
  emptyMessage: string;
  /** MCP modules Luma may read (write tools are stripped server-side for demos). */
  enabledModules?: string[];
  width?: string;
  autoFocus?: boolean;
}

/** A stable-ish id for client-only messages (no server seq in one-shot mode). */
function localId(prefix: string, n: number): string {
  return `demo-${prefix}-${n}`;
}

export function DemoLumaPanel({
  open,
  onClose,
  title,
  inputPlaceholder,
  emptyMessage,
  enabledModules,
  width = 'w-full sm:w-[480px]',
  autoFocus = true,
}: DemoLumaPanelProps) {
  const { t } = useTranslation('aiChat');
  const { isRtl } = useDirection();
  useResponsive();
  const panelSide = isRtl ? 'left' : 'right';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<StreamingState>({ isGenerating: false });
  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming.isGenerating) return;

      const userSeq = seqRef.current++;
      const userMsg: ChatMessage = {
        id: localId('user', userSeq),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setStreaming({ isGenerating: true, response: '' });

      const controller = new AbortController();
      abortRef.current = controller;
      let accumulated = '';

      try {
        const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
        const res = await fetch(`${API_BASE_URL}/api/v1/ai-chat/message`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: trimmed,
            stream: true,
            ...(enabledModules && enabledModules.length > 0
              ? { enabled_modules: enabledModules }
              : {}),
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          throw new Error(`one-shot error ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let done = false;
        while (!done) {
          const { done: streamDone, value } = await reader.read();
          if (streamDone) break;
          buffer += decoder.decode(value, { stream: true });
          let sep: number;
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const dataLine = rawEvent
              .split('\n')
              .find((l) => l.startsWith('data:'));
            if (!dataLine) continue;
            const payload = dataLine.slice(5).trim();
            if (payload === '[DONE]') {
              done = true;
              break;
            }
            try {
              accumulated += JSON.parse(payload) as string;
            } catch {
              /* ignore malformed chunk */
            }
            setStreaming({ isGenerating: true, response: accumulated });
          }
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          // Stopped by the user — keep whatever streamed so far.
        } else {
          accumulated = accumulated || t('errors.generationFailed', { defaultValue: 'Sorry, something went wrong. Please try again.' });
        }
      } finally {
        const assistantSeq = seqRef.current++;
        setMessages((prev) => [
          ...prev,
          {
            id: localId('assistant', assistantSeq),
            role: 'assistant',
            content: accumulated,
            timestamp: new Date().toISOString(),
          },
        ]);
        setStreaming({ isGenerating: false });
        abortRef.current = null;
      }
    },
    [streaming.isGenerating, enabledModules, t],
  );

  return (
    <Sheet
      open={open}
      modal={false}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <SheetContent
        side={panelSide}
        className={`${width} p-0 gap-0 flex flex-col overflow-hidden`}
        hideCloseButton
        hideOverlay
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <SheetTitle>{title}</SheetTitle>
        </VisuallyHidden>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <MessageSquare className="h-5 w-5 text-primary shrink-0" />
            <span className="font-semibold truncate">{title}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <ChatBox
            messages={messages}
            onSend={handleSend}
            onStop={handleStop}
            streaming={streaming}
            height="100%"
            emptyMessage={emptyMessage}
            inputPlaceholder={inputPlaceholder}
            autoFocus={autoFocus && open}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
